import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Check, CheckSquare, Flag, FolderKanban, Mail, Phone, UserRound } from "lucide-react"
import { api } from "@/lib/api"
import { toast } from "@/components/portal/Toast"
import { Loading } from "@/components/portal/LoadingError"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { zustandVon, nummerLesbar } from "@/pages/KiTelefonNeuPage"
import type { Call, TelefonData } from "@/pages/KiTelefonPage"
import type { ToepfeZeile } from "@/pages/emails/PostfachKarte"
import { mitAlter, prioAnruf, prioAufgabe, prioMail, prioVerpasst, vergleich, zuerst, type Prio } from "@/pages/todoPrioritaet"
import "./todo.css"

/**
 * To-Do (Paul 02.11.2026, Spec docs/spec_todo_seite.md, #198): was heute zu
 * tun ist, aus vier Quellen in einer ruhigen Liste.
 *
 * 🔑 Keine zweite Wahrheit: Anrufe kommen aus der KI-Telefon-Liste (dieselbe
 * Regel `zustandVon`), Mails aus den Postfach-Töpfen, Aufgaben aus den
 * Internen Aufgaben. Neu sind nur Vorgänge ohne Termin und verpasste
 * Team-Anrufe (`/api/v1/todo/*`). „Erledigt“ schreibt in die Quelle zurück.
 * Jeder Block lädt für sich — fällt eine Quelle aus, steht das an ihrem Block,
 * nie „0 offen“.
 */

export type Art = "anrufe" | "mails" | "projekte" | "aufgaben"

type Frage = { text: string; ja: string; aktion: () => Promise<void> }

export type Eintrag = {
  key: string
  art: Art
  titel: string
  text?: string | null
  quelle?: string | null
  zeit?: string | null
  betrieb?: string | null
  wartet?: string | null
  bereit?: string | null
  fuer?: string | null
  /** Stufe + sichtbarer Grund (`todoPrioritaet`). */
  prio: Prio
  haupt: { label: string; primaer?: boolean; los: () => void | Promise<void>; frageDanach?: string; frageVorher?: Frage }
  /** Haken: schreibt „erledigt“ in die Quelle; ohne = kein Haken (HERO-Vorgang). */
  erledigen?: () => Promise<void>
  rueckgaengig?: () => Promise<void>
}

type Aufgabe = {
  id: number; tenant_slug: string | null; titel: string; notiz: string | null
  faellig_am: string | null; zustaendig: string | null; prioritaet?: string | null
}
type Verpasst = {
  ids: number[]; tenant_slug: string; nummer: string | null; kontakt: string | null
  headline: string | null; zusammenfassung: string | null; zuletzt: string | null; anzahl: number
}
type Vorgang = {
  id: number; tenant_slug: string; project_nr: string | null; titel: string | null
  kontakt: string | null; status_name: string; status_seit: string | null
}
type MailZeile = ToepfeZeile & { tenant_slug?: string | null; handlungsbedarf?: string | null; category?: string | null }

const TAG = 86_400_000
const MAX_JE_BLOCK = 5

async function senden(method: string, url: string, body?: unknown) {
  const r = await fetch(url, {
    method, credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  const d = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`)
  return d
}

async function holen<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json() as Promise<T>
}

function mitTenant(url: string, tenant: string) {
  return tenant ? `${url}${url.includes("?") ? "&" : "?"}tenant=${encodeURIComponent(tenant)}` : url
}

/** „heute 07:42“, „gestern 16:20“, „Mo 27.10.“ */
export function wann(iso?: string | null, jetzt = new Date()): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const tage = Math.round((new Date(jetzt).setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / TAG)
  const uhr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  if (tage === 0) return `heute ${uhr}`
  if (tage === 1) return `gestern ${uhr}`
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })
}

/** Bernstein-Schild ab einem Werktag Wartezeit; darunter nichts. */
export function wartetSeit(iso?: string | null, jetzt = new Date()): string | null {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  const tage = Math.floor((jetzt.getTime() - t) / TAG)
  if (tage < 1) return null
  if (tage === 1) return "wartet seit gestern"
  if (tage < 14) return `seit ${tage} Tagen`
  return `seit ${Math.floor(tage / 7)} Wochen`
}

/** Eine Rückruf-Aufgabe aus dem KI Telefon (#151) steht schon als Anruf da. */
export function istRueckrufAufgabe(a: Aufgabe, callIds: Set<string>): boolean {
  const m = /\(Voice-Call ([^)\s]+)\)/.exec(a.notiz || "")
  return !!m && callIds.has(m[1])
}

function heuteIso() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

const BLOECKE: { art: Art; titel: string; icon: typeof Phone }[] = [
  { art: "anrufe", titel: "Anrufe sichten", icon: Phone },
  { art: "mails", titel: "E-Mails, die dich brauchen", icon: Mail },
  { art: "projekte", titel: "Projekte in HERO", icon: FolderKanban },
  { art: "aufgaben", titel: "Interne Aufgaben", icon: CheckSquare },
]

function gruss() {
  const h = new Date().getHours()
  return h < 11 ? "Guten Morgen" : h < 18 ? "Guten Tag" : "Guten Abend"
}

export default function TodoPage() {
  const [tenant, setTenant] = useTenantFilter()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [filter, setFilter] = useState<Art | "alle">("alle")
  const [nurMeine, setNurMeine] = useState(false)
  const [frage, setFrage] = useState<{ key: string } & Frage | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [weg, setWeg] = useState<Set<string>>(new Set())
  const [ganz, setGanz] = useState<Set<Art>>(new Set())

  const tenants = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/tenants", {})
      if (error) throw new Error(String(error))
      return data
    },
    staleTime: 1000 * 60 * 5,
  })
  // Dieselben Schlüssel wie KI Telefon und Postfach: was dort passiert, lädt hier neu.
  const telefon = useQuery<TelefonData>({
    queryKey: ["ki-telefon-data", tenant],
    queryFn: () => holen(mitTenant("/admin/ki-telefon/api/data", tenant)),
    refetchInterval: 60_000,
  })
  const verpasst = useQuery<{ items: Verpasst[] }>({
    queryKey: ["todo", "verpasst", tenant],
    queryFn: () => holen(mitTenant("/api/v1/todo/verpasste-anrufe", tenant)),
    refetchInterval: 60_000,
  })
  const mails = useQuery<{ klick: MailZeile[]; hand: MailZeile[]; ich?: string | null }>({
    queryKey: ["emails", "toepfe-todo", tenant],
    queryFn: () => holen(mitTenant("/api/v1/emails/toepfe", tenant)),
    refetchInterval: 60_000,
  })
  const vorgaenge = useQuery<{ items: Vorgang[] }>({
    queryKey: ["todo", "vorgaenge", tenant],
    queryFn: () => holen(mitTenant("/api/v1/todo/vorgaenge-ohne-termin", tenant)),
  })
  const aufgaben = useQuery<{ items: Aufgabe[]; ich: string }>({
    queryKey: ["portal-aufgaben", "todo", tenant],
    queryFn: () => holen(mitTenant("/api/v1/portal-aufgaben?offen=true", tenant)),
    refetchInterval: 60_000,
  })

  const ich = aufgaben.data?.ich || mails.data?.ich || ""
  const name = useMemo(() => {
    const m = new Map<string, string>()
    tenants.data?.items.forEach((t) => m.set(t.slug, t.name))
    return (slug?: string | null) => (slug ? m.get(slug) || slug : null)
  }, [tenants.data])

  const neuLaden = () => {
    for (const k of [["ki-telefon-data"], ["todo"], ["emails"], ["portal-aufgaben"]]) qc.invalidateQueries({ queryKey: k })
  }

  const eintraege = useMemo<Record<Art, Eintrag[]>>(() => {
    const jetzt = new Date()
    const offeneCalls: Call[] = (telefon.data?.calls || []).filter((c) => c.status !== "in_progress" && zustandVon(c).zuTun)
    const callIds = new Set(offeneCalls.map((c) => c.call_id))

    const anrufe: Eintrag[] = [
      ...offeneCalls.map((c): Eintrag => {
        const z = zustandVon(c)
        const prio = mitAlter(prioAnruf(c, z.key), c.started_at, jetzt)
        // Sagt das Schild schon dasselbe („heute zurückrufen“), fällt die Zeile weg.
        const zustandText = c.ai_headline && z.label.toLowerCase() !== prio.grund?.toLowerCase() ? z.label : null
        const wer = c.contact_name || nummerLesbar(c.caller_number)
        const url = `/admin/ki-telefon/api/call/${encodeURIComponent(c.call_id)}/erledigt`
        return {
          key: `call:${c.call_id}`, art: "anrufe",
          titel: c.ai_headline || z.label, text: zustandText,
          quelle: `Telefonbot · ${wer}`, zeit: c.started_at, betrieb: c.portal_tenant_slug || c.tenant_slug,
          wartet: wartetSeit(c.started_at, jetzt), fuer: c.zugewiesen_an || null,
          prio,
          haupt: { label: "Ansehen", los: () => navigate(`/ki-telefon/${encodeURIComponent(c.call_id)}`) },
          erledigen: () => senden("POST", url, { erledigt: true }),
          rueckgaengig: () => senden("POST", url, { erledigt: false }),
        }
      }),
      ...(verpasst.data?.items || []).map((v): Eintrag => {
        const nr = v.nummer && /\d/.test(v.nummer) ? v.nummer : null
        const alle = (status: string) => Promise.all(v.ids.map((id) =>
          senden("PATCH", `/api/v1/sipgate/calls/${id}`, { status }))).then(() => undefined)
        return {
          key: `verpasst:${v.ids[0]}`, art: "anrufe",
          titel: v.headline || (v.anzahl > 1 ? `Verpasster Anruf (${v.anzahl}×)` : "Verpasster Anruf"),
          text: v.zusammenfassung || "Niemand hat abgenommen, keine Nachricht.",
          quelle: `Telefonanlage · ${v.kontakt || (nr ? nummerLesbar(nr) : "Nummer unterdrückt")}`,
          zeit: v.zuletzt, betrieb: v.tenant_slug, wartet: wartetSeit(v.zuletzt, jetzt),
          prio: mitAlter(prioVerpasst(v.anzahl), v.zuletzt, jetzt),
          haupt: nr
            ? { label: "Zurückrufen", los: () => { window.location.href = `tel:${nr.replace(/[^\d+]/g, "")}` }, frageDanach: "Hast du jemanden erreicht?" }
            : { label: "Ansehen", los: () => navigate("/sipgate") },
          erledigen: () => alle("done"),
          rueckgaengig: () => alle("missed"),
        }
      }),
    ]

    const mailListe: Eintrag[] = [...(mails.data?.klick || []), ...(mails.data?.hand || [])].map((m): Eintrag => ({
      key: `mail:${m.id}`, art: "mails",
      titel: m.subject || "(ohne Betreff)", text: m.handlungsbedarf || m.kurz,
      quelle: m.sender || m.sender_email, zeit: m.received_at, betrieb: m.tenant_slug,
      wartet: wartetSeit(m.received_at, jetzt), bereit: m.topf === "klick" ? m.kurz : null, fuer: m.zustaendig || null,
      prio: mitAlter(prioMail(m, jetzt), m.received_at, jetzt),
      haupt: {
        label: m.topf === "klick" ? "Vorschlag ansehen" : "Öffnen", primaer: m.topf === "klick",
        los: () => navigate(`/emails?open=${m.id}${m.tenant_slug ? `&tenant=${encodeURIComponent(m.tenant_slug)}` : ""}`),
      },
      erledigen: () => senden("PATCH", `/api/v1/emails/${m.id}`, { status: "done" }),
      rueckgaengig: () => senden("PATCH", `/api/v1/emails/${m.id}`, { status: "reviewed" }),
    }))

    const projekte: Eintrag[] = (vorgaenge.data?.items || []).map((p): Eintrag => ({
      key: `vorgang:${p.tenant_slug}:${p.id}`, art: "projekte",
      titel: `${p.project_nr || "Vorgang"} · Termin fehlt`,
      text: `Steht auf „${p.status_name}“, aber am Vorgang hängt kein Termin mehr — vermutlich in HERO gelöscht.`,
      quelle: ["HERO", p.kontakt || p.titel].filter(Boolean).join(" · "),
      zeit: p.status_seit, betrieb: p.tenant_slug, wartet: wartetSeit(p.status_seit, jetzt),
      prio: mitAlter({ stufe: 3, grund: null }, p.status_seit, jetzt),
      haupt: {
        label: "Archivieren", primaer: true, los: () => undefined,
        frageVorher: {
          text: "In HERO auf „Archiviert“ setzen? Soll der Kunde doch einen Termin bekommen, öffne lieber den Vorgang.",
          ja: "Ja, archivieren",
          aktion: async () => {
            await senden("POST", `/api/v1/todo/vorgaenge-ohne-termin/${encodeURIComponent(p.tenant_slug)}/${p.id}/archivieren`)
            toast(`${p.project_nr || "Vorgang"} archiviert`, "success")
          },
        },
      },
    }))

    const heute = heuteIso()
    const aufgabenListe: Eintrag[] = (aufgaben.data?.items || [])
      .filter((a) => !istRueckrufAufgabe(a, callIds))
      .map((a): Eintrag => {
        const url = `/api/v1/portal-aufgaben/${a.id}`
        const ueber = a.faellig_am && a.faellig_am < heute
        return {
          key: `aufgabe:${a.id}`, art: "aufgaben",
          titel: a.titel, text: a.notiz ? a.notiz.replace(/\(Voice-Call [^)]+\)/, "").trim().slice(0, 180) : null,
          quelle: "Interne Aufgabe",
          zeit: null, betrieb: a.tenant_slug,
          wartet: ueber ? `überfällig seit ${new Date(a.faellig_am!).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}`
            : a.faellig_am === heute ? "fällig heute" : null,
          fuer: a.zustaendig || null,
          prio: prioAufgabe(a, heute),
          haupt: { label: "Öffnen", los: () => navigate("/interne-aufgaben") },
          erledigen: () => senden("PATCH", url, { erledigt: true }),
          rueckgaengig: () => senden("PATCH", url, { erledigt: false }),
        }
      })

    // Wichtiges zuerst, bei gleicher Stufe wer am längsten wartet (`todoPrioritaet`).
    return { anrufe: anrufe.sort(vergleich), mails: mailListe.sort(vergleich), projekte: projekte.sort(vergleich), aufgaben: aufgabenListe.sort(vergleich) }
  }, [telefon.data, verpasst.data, mails.data, vorgaenge.data, aufgaben.data, navigate])

  const sichtbar = (e: Eintrag) => !weg.has(e.key) && (!nurMeine || (!!ich && e.fuer === ich))
  const zahl = (art: Art) => eintraege[art].filter(sichtbar).length
  const offen = BLOECKE.reduce((s, b) => s + zahl(b.art), 0)
  const bereit = eintraege.mails.filter((e) => sichtbar(e) && e.bereit).length
  const meine = ich ? BLOECKE.reduce((s, b) => s + eintraege[b.art].filter((e) => !weg.has(e.key) && e.fuer === ich).length, 0) : 0
  const status: Record<Art, { laedt: boolean; fehler: boolean }> = {
    anrufe: { laedt: telefon.isLoading || verpasst.isLoading, fehler: !!telefon.error || !!verpasst.error },
    mails: { laedt: mails.isLoading, fehler: !!mails.error },
    projekte: { laedt: vorgaenge.isLoading, fehler: !!vorgaenge.error },
    aufgaben: { laedt: aufgaben.isLoading, fehler: !!aufgaben.error },
  }
  const alleGeladen = BLOECKE.every((b) => !status[b.art].laedt)
  const irgendwoFehler = BLOECKE.some((b) => status[b.art].fehler)

  const abhaken = async (e: Eintrag) => {
    if (!e.erledigen) return
    setLaeuft(e.key)
    try {
      await e.erledigen()
      setWeg((s) => new Set(s).add(e.key))
      setFrage(null)
      const zurueck = e.rueckgaengig
      toast("Erledigt", "success", zurueck ? {
        label: "Rückgängig",
        onClick: () => {
          zurueck().then(() => { setWeg((s) => { const n = new Set(s); n.delete(e.key); return n }); neuLaden() })
            .catch((err) => toast("Zurückholen fehlgeschlagen: " + (err instanceof Error ? err.message : String(err)), "error"))
        },
      } : undefined)
      neuLaden()
    } catch (err) {
      toast("Fehlgeschlagen: " + (err instanceof Error ? err.message : String(err)), "error")
    } finally {
      setLaeuft(null)
    }
  }

  const hauptKlick = async (e: Eintrag) => {
    if (e.haupt.frageVorher) { setFrage({ key: e.key, ...e.haupt.frageVorher }); return }
    await e.haupt.los()
    if (e.haupt.frageDanach && e.erledigen) {
      setFrage({ key: e.key, text: e.haupt.frageDanach, ja: "Ja, erledigt", aktion: () => abhaken(e) })
    }
  }

  const frageJa = async () => {
    if (!frage) return
    const key = frage.key
    setLaeuft(key)
    try {
      await frage.aktion()
      setWeg((s) => new Set(s).add(key))
      setFrage(null)
      neuLaden()
    } catch (err) {
      toast("Fehlgeschlagen: " + (err instanceof Error ? err.message : String(err)), "error")
    } finally {
      setLaeuft(null)
    }
  }

  const chips: { art: Art | "alle"; label: string; n: number }[] = [
    { art: "alle", label: "Alles", n: offen },
    { art: "anrufe", label: "Anrufe", n: zahl("anrufe") },
    { art: "mails", label: "E-Mails", n: zahl("mails") },
    { art: "projekte", label: "Projekte", n: zahl("projekte") },
    { art: "aufgaben", label: "Aufgaben", n: zahl("aufgaben") },
  ]
  // „Zuerst“: die wichtigsten drei über alle Quellen — nur in „Alles“, und
  // dort nicht noch einmal im eigenen Block.
  const oben = filter === "alle" ? zuerst(BLOECKE.flatMap((b) => eintraege[b.art].filter(sichtbar))) : []
  const obenKeys = new Set(oben.map((e) => e.key))

  const zeile = (e: Eintrag) => (
    <div key={e.key} className="td-zeile">
      {e.erledigen ? (
        <button className="td-haken" onClick={() => abhaken(e)} disabled={laeuft === e.key} aria-label={`„${e.titel}“ als erledigt abhaken`}>
          <span className="dot"><Check className="w-3.5 h-3.5" strokeWidth={3} aria-hidden="true" /></span>
        </button>
      ) : <span className="td-haken-platz" aria-hidden="true" />}
      <div className="td-inhalt">
        <span className="td-titel">{e.titel}</span>
        {e.text && <p className="td-text">{e.text}</p>}
        <div className="td-meta">
          {e.quelle && <span>{e.quelle}</span>}
          {e.zeit && <><span aria-hidden="true">·</span><span>{wann(e.zeit)}</span></>}
          {!nurMeine && e.fuer && <span className="td-fuer">{e.fuer === ich ? "für dich" : `für ${e.fuer}`}</span>}
          {!tenant && e.betrieb && <span className="td-schild betrieb">{name(e.betrieb)}</span>}
          {e.prio.grund && <span className={`td-schild grund stufe-${e.prio.stufe}`}>{e.prio.grund}</span>}
          {e.wartet && <span className="td-schild wartet">{e.wartet}</span>}
          {e.bereit && <span className="td-schild bereit">Vorschlag: {e.bereit}</span>}
        </div>
        {frage?.key === e.key && (
          <div className="td-frage" role="group" aria-label="Rückfrage">
            <span>{frage.text}</span>
            <button className="td-btn ruhig" onClick={() => setFrage(null)}>{e.haupt.frageVorher ? "Abbrechen" : "Noch offen"}</button>
            <button className="td-btn gruen" onClick={frageJa} disabled={laeuft === e.key}>{frage.ja}</button>
          </div>
        )}
      </div>
      <button className={`td-btn ${e.haupt.primaer ? "primaer" : "ruhig"}`} onClick={() => hauptKlick(e)}>
        {e.haupt.label}
      </button>
    </div>
  )

  const bloecke = BLOECKE.filter((b) => filter === "alle" || filter === b.art)
    .filter((b) => status[b.art].laedt || status[b.art].fehler
      || eintraege[b.art].some((e) => sichtbar(e) && !obenKeys.has(e.key)))

  return (
    <div className="td-wrap">
      <header className="td-kopf">
        <div className="td-kopf-zeile">
          <div>
            <span className="td-datum">{new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}</span>
            <h2 className="td-gruss">{gruss()}{ich ? `, ${ich.charAt(0).toUpperCase()}${ich.slice(1)}` : ""}.</h2>
            {alleGeladen && offen > 0 && (
              <p className="td-satz">
                <strong>{offen} {offen === 1 ? "Ding braucht" : "Dinge brauchen"}</strong> dich.
                {bereit > 0 && ` ${bereit} ${bereit === 1 ? "Mail hat" : "Mails haben"} schon einen fertigen Vorschlag.`}
              </p>
            )}
          </div>
          <div className="td-seg" role="group" aria-label="Betrieb">
            <button aria-pressed={!tenant} onClick={() => setTenant("")}>Alle Betriebe</button>
            {(tenants.data?.items || []).map((t) => (
              <button key={t.slug} aria-pressed={tenant === t.slug} onClick={() => setTenant(t.slug)}>{t.name}</button>
            ))}
          </div>
        </div>
        <div className="td-chips" role="group" aria-label="Art filtern">
          {chips.map((c) => (
            <button key={c.art} className="td-chip" aria-pressed={filter === c.art} onClick={() => { setFilter(c.art); setFrage(null) }}>
              {/* Beim Laden keine Zahl — „0“ hieße „nichts offen“. */}
              {c.label} {(c.art === "alle" ? alleGeladen : !status[c.art].laedt) && <span className="n">{c.n}</span>}
            </button>
          ))}
          {ich && alleGeladen && (
            <button className="td-chip td-chip-rechts" aria-pressed={nurMeine} onClick={() => { setNurMeine(!nurMeine); setFrage(null) }}>
              <UserRound className="w-3.5 h-3.5" aria-hidden="true" /> Nur meine <span className="n">{meine}</span>
            </button>
          )}
        </div>
      </header>

      {!alleGeladen && bloecke.length === 0 && <Loading label="Sammle, was offen ist…" />}

      {oben.length > 0 && (
        <section className="td-block td-zuerst" aria-label="Zuerst">
          <div className="td-block-kopf">
            <span className="td-block-icon"><Flag className="w-4 h-4" aria-hidden="true" /></span>
            <h3>Zuerst</h3>
          </div>
          <div className="td-karte">{oben.map(zeile)}</div>
        </section>
      )}

      {bloecke.map((b) => {
        const liste = eintraege[b.art].filter((e) => sichtbar(e) && !obenKeys.has(e.key))
        // Je Block höchstens fünf — sonst ist die Seite bei echter Menge
        // (28 offene Anrufe auf dev) wieder die lange Liste, die sie ersetzen soll.
        const zeigen = ganz.has(b.art) ? liste : liste.slice(0, MAX_JE_BLOCK)
        const Icon = b.icon
        return (
          <section key={b.art} className="td-block" aria-label={b.titel}>
            <div className="td-block-kopf">
              <span className="td-block-icon"><Icon className="w-4 h-4" aria-hidden="true" /></span>
              <h3>{b.titel}</h3>
              {!status[b.art].laedt && !status[b.art].fehler && <span className="n">{liste.length}</span>}
            </div>
            <div className="td-karte">
              {status[b.art].laedt && <div className="td-hinweis">Lädt…</div>}
              {status[b.art].fehler && (
                <div className="td-hinweis fehler" role="alert">Gerade nicht ladbar — die anderen Bereiche stimmen trotzdem.</div>
              )}
              {zeigen.map(zeile)}
              {liste.length > MAX_JE_BLOCK && (
                <button className="td-mehr" aria-expanded={ganz.has(b.art)} onClick={() => setGanz((s) => {
                  const n = new Set(s)
                  if (n.has(b.art)) n.delete(b.art)
                  else n.add(b.art)
                  return n
                })}>
                  {ganz.has(b.art) ? "Weniger anzeigen" : `Alle ${liste.length} anzeigen`}
                </button>
              )}
            </div>
          </section>
        )
      })}

      {alleGeladen && !irgendwoFehler && bloecke.length === 0 && oben.length === 0 && (
        <div className="td-leer">
          <span className="td-leer-icon"><Check className="w-7 h-7" strokeWidth={2.5} aria-hidden="true" /></span>
          <h3>Alles erledigt.</h3>
          <p>Gerade wartet nichts {nurMeine ? "auf dich" : "auf euch"}. Neue Mails, Anrufe und Projekte erscheinen hier, sobald sie jemanden brauchen.</p>
        </div>
      )}
    </div>
  )
}
